import React, {
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { createContext } from 'use-context-selector';
import { getVeiculosServices } from 'services/modulos/getVeiculos';
import { PositionVehicle, ResumeVehicle } from 'interfaces/vehicle';
import { FencesData } from 'interfaces/fences';
import { ServiceCerca } from 'services/modulos/serviceCerca';
import { ServiceVeiculo } from 'services/modulos/service_veiculo';
import { useAuthData } from 'hooks/auth/useAuthData';
import { ServiceEstatisticas } from 'services/modulos/serviceEstatisticas';
import { FenceSupesp } from 'interfaces/statistics';

type TVehicles = {
  [key: string]: ResumeVehicle;
};
export interface GlobalProps {
  vehicles: TVehicles;
  // lastPositions?: ;
  loadLastPositions: () => Promise<PositionVehicle[]>;
  fences: MutableRefObject<FencesData>;
  statistics: FenceSupesp[];
  loadStatistics: (params: {
    cidades: string[];
    tipoArquivos: string[];
  }) => void;
  handleClearStatistics: () => void;
  loadingStatistics: boolean;
  setLoadingStatistics: Dispatch<SetStateAction<boolean>>;
  loadingRequest?: {
    vehicles: boolean;
    // lastPositions: boolean;
    fences: boolean;
  };
  loadFences: () => Promise<void>;
  handleRenderFencesName: (render: boolean, map: google.maps.Map) => void;
  showFencesName: MutableRefObject<boolean>;
}

export const globalContext = createContext({} as GlobalProps);

export const GlobalProvider = ({ children }: { children: ReactNode }) => {
  const [vehicles, setVehicles] = useState<TVehicles>({});
  const [statistics, setStatistics] = useState<FenceSupesp[]>([]);
  // const [lastPositions, setLastPositions] = useState<PositionVehicle[]>([]);

  const showFencesName = useRef(false);

  const fences = useRef<FencesData>({
    data: [],
    mapFences: [],
    idList: [],
  });

  const [loadingStatistics, setLoadingStatistics] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState({
    vehicles: false,
    // lastPositions: false,
    fences: false,
  });
  const { data: dataAuth } = useAuthData();
  const { isFenceAdmin, roles } = dataAuth || {};
  const hasRoleFence = roles?.includes('ROLE_ROTAS_VISUALIZACAO_CERCA');

  const updateLoadingRequest = useCallback((newProperties: any) => {
    setLoadingRequest(prev => ({ ...prev, ...newProperties }));
  }, []);

  const loadLastPositions = useCallback(async () => {
    try {
      const res = await getVeiculosServices.get();

      return res;
    } catch (e) {
      //
      return [];
    }
  }, []);

  const initFetch: () => Promise<void> = useCallback(async (): Promise<
    void
  > => {
    try {
      updateLoadingRequest({
        vehicles: true,
        // lastPositions: true,
        fences: true,
      });

      let requests: { name: string; func: (params?: any) => any }[] = [
        {
          name: 'resumo-veiculo',
          func: ServiceVeiculo.resumoVeiculo,
        },
        // getVeiculosServices.get,
      ];

      if (hasRoleFence) {
        if (isFenceAdmin) {
          requests.push({
            name: 'cercas-get',
            func: ServiceCerca.getCercaAdminAll,
          });
        } else {
          requests.push({
            name: 'cercas-get',
            func: ServiceCerca.getCercaAll,
          });
        }
      }

      await Promise.all(
        requests.map(p => {
          p.func().then((response: any) => {
            if (p.name === 'resumo-veiculo') {
              updateLoadingRequest({
                vehicles: false,
              });

              let obj: TVehicles = {};

              response.map((v: ResumeVehicle) => {
                obj[v.equipamento || ''] = v;
              });

              return setVehicles(obj);
            }
            // if (p === getVeiculosServices.get) {
            //   updateLoadingRequest({
            //     lastPositions: false,
            //   });
            //   return setLastPositions(response);
            // }
            if (Array.isArray(response)) {
              fences.current = {
                ...fences.current,
                data: response,
              };
              updateLoadingRequest({
                fences: false,
              });
              return;
            }
          });
        })
      );
    } catch (err) {
      console.error(err);
    }
  }, [vehicles, fences.current]);

  const loadFences: () => Promise<void> = useCallback(async (): Promise<
    void
  > => {
    try {
      updateLoadingRequest({ fences: true });
      let allFences: any[] = [];
      if (hasRoleFence) {
        if (isFenceAdmin) {
          allFences = await ServiceCerca.getCercaAdminAll();
        } else {
          allFences = await ServiceCerca.getCercaAll();
        }
      }

      fences.current = {
        ...fences.current,
        data: allFences,
      };
    } catch (err) {
      console.log(err);
    } finally {
      updateLoadingRequest({ fences: false });
    }
  }, []);

  const loadStatistics = useCallback(
    async ({
      cidades,
      tipoArquivos,
    }: {
      cidades: string[];
      tipoArquivos: string[];
    }) => {
      try {
        setLoadingStatistics(true);
        const response = await ServiceEstatisticas.get({
          cidades,
          tipoArquivos,
        });

        setStatistics(response);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingStatistics(false);
      }
    },
    []
  );

  const handleClearStatistics = useCallback(() => {
    setStatistics([]);
  }, []);

  const handleRenderFencesName = useCallback(
    (render: boolean, map: google.maps.Map) => {
      showFencesName.current = render;

      if (!render) {
        fences.current.mapFences.map(fence => {
          fence.infoWindow.close();
        });
      } else {
        fences.current.mapFences.map(fence => {
          fence.infoWindow.open(map);
        });
      }
    },
    [fences.current]
  );

  useEffect(() => {
    initFetch();
  }, []);

  return (
    <>
      <globalContext.Provider
        value={{
          vehicles,
          loadLastPositions,
          fences,
          statistics,
          loadStatistics,
          handleClearStatistics,
          loadingStatistics,
          setLoadingStatistics,
          loadingRequest,
          loadFences,
          handleRenderFencesName,
          showFencesName,
        }}
      >
        {children}
      </globalContext.Provider>
    </>
  );
};
